import { Alert, Grid, IconButton, Snackbar, TextField, Tooltip, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../orders/Orders.scss';
import { Delete, Edit, Email, Link as MuiLinkIcon, Redeem } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import '../orders/UpcomingOrders.scss';
import moment from 'moment/moment';
import {
  AccountData,
  OrderService,
  RefundOwnerType,
  VoucherOut,
  VoucherService,
  VoucherStatus,
  VoucherType,
} from '../api';
import { VoucherPaymentStatus } from '../api/models/VoucherPaymentStatus';
import { useVouchersFilter } from './VouchersFilter/VouchersFilterContext';
import { getVoucherPaymentStatusTextOrIcon, getVoucherStatus } from './utils';
import { ReactComponent as MonetaryVoucherIcon } from '../images/voucherIcons/mb-voucher-monetary.svg';
import { ReactComponent as ExperienceVoucherIcon } from '../images/voucherIcons/mb-voucher-experience.svg';
import { ReactComponent as LinkedOrderIcon } from '../images/voucherIcons/mb-linked-order.svg';
import { CellProps, useTable } from '../table/useTable';
import VouchersActions from './VouchersActions';
import { useAuth } from '../session/InternalAuthProvider';
import { useCalendar } from '../calendar/CalendarContext/CalendarContext';
import EditOrderDialog from '../calendar/dialogs/EditOrderDialog/EditOrderDialog';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { useLanguage } from '../common/GeneralUtils';
import TimeUtils from '../utils/TimeUtils';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import ConfirmationDialog from '../common/ui/ConfimationDialog';
import { RefundOfferDialog, RefundOfferTarget } from '../refunds/RefundOfferDialog';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import CopyToClipboardButton from '../calendar/dialogs/CopyToClipboardButton';
import { TABLE_PRICE_ID } from '../table/TableComponent';
import { customerFacingPriceFormatter } from '../utils/utils';

type VoucherFilterFetch = {
  statuses?: VoucherStatus[];
  paymentStatuses?: VoucherPaymentStatus[];
  orderPageIds?: string[] | null;
  types?: VoucherType[];
};

export const voucherFetcher = async ({
  search,
  page,
  perPage,
  filterState,
}: {
  search?: string;
  page: number;
  perPage: number;
  filterState?: VoucherFilterFetch;
}) =>
  VoucherService.getVouchers(
    page,
    perPage,
    search,
    filterState?.statuses,
    filterState?.paymentStatuses,
    filterState?.orderPageIds ?? undefined,
    filterState?.types,
  );

const VouchersTable = React.memo(() => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isAtLeastSupervisor, readOnly } = UseAuthPermissions();

  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const [deleteVoucherIsOpen, setDeleteVoucherIsOpen] = useState(false);
  const [refundOffer, setRefundOffer] = useState<RefundOfferTarget | undefined>(undefined);
  const [sendVoucherDialogIsOpen, setSendVoucherDialogIsOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<VoucherOut | undefined>(undefined);
  const [sendToEmail, setSendToEmail] = useState<string | undefined>();
  const [emailSentIsOpen, setEmailSentIsOpen] = useState(false);

  const timeFormat = getTimeFormat(account);
  const { editOrderDrawerState, closeEditOrderDrawer, openEditOrderDrawer } = useCalendar();
  const openRedeemedOrderDrawer = async (orderId: string) => {
    const order = await OrderService.getOrder(orderId);
    openEditOrderDrawer({
      order,
      orderPageId: order.order_page_id,
      voucherToEdit: undefined,
      voucherToRedeem: undefined,
      createVoucher: false,
    });
  };

  function formatCreatedAt(created_at: string) {
    const createdAt = TimeUtils.getLocalTime(created_at, account.timezone);
    const isToday = createdAt.isSame(moment(), 'day');
    return (
      <Typography
        variant={isToday ? 'body1' : 'body2'}
        color={isToday ? 'primary.main' : undefined}
        fontWeight={isToday ? 700 : undefined}>
        {createdAt.format(isToday ? timeFormat : `${getDateFormat(account).longDateFormat} ${timeFormat}`)}
      </Typography>
    );
  }

  const tableDetails: CellProps<VoucherOut>[] = [
    {
      id: 'edit',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (voucher: VoucherOut) => (
        <IconButton
          disabled={
            !isAtLeastSupervisor ||
            (voucher.type === VoucherType.MONETARY
              ? voucher.status === VoucherStatus.PENDING_PAYMENT
              : voucher.status !== VoucherStatus.ACTIVE)
          }
          onClick={() => {
            openEditOrderDrawer({
              orderPageId: voucher.order_page_id,
              createVoucher: false,
              voucherToEdit: voucher,
            });
          }}>
          <Tooltip title={t('edit')} arrow>
            <Edit />
          </Tooltip>
        </IconButton>
      ),
    },
    {
      id: 'createdat',
      alignment: 'center',
      label: t('vouchers.createdat'),
      render: (voucher: VoucherOut) => formatCreatedAt(voucher.created_at),
    },
    {
      id: 'name',
      alignment: 'center',
      label: t('vouchers.name'),
      render: (voucher: VoucherOut) => `${voucher.firstname} ${voucher.lastname}`,
    },
    {
      id: 'type',
      alignment: 'center',
      label: t('vouchers.type'),
      render: (voucher: VoucherOut) => {
        const VoucherTypeIcon = voucher.type === VoucherType.MONETARY ? MonetaryVoucherIcon : ExperienceVoucherIcon;
        return (
          <Tooltip title={t(`vouchers.types.${voucher.type || VoucherType.EXPERIENCE}`)} arrow>
            <span style={{ display: 'inline-flex' }}>
              <VoucherTypeIcon
                width={24}
                height={24}
                aria-label={t(`vouchers.types.${voucher.type || VoucherType.EXPERIENCE}`)}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      id: 'num_persons',
      alignment: 'center',
      label: t('vouchers.num_persons'),
      render: (voucher: VoucherOut) => voucher.persons,
    },
    {
      id: 'phone_of_buyer',
      alignment: 'center',
      label: t('vouchers.phone_of_buyer'),
      render: (voucher: VoucherOut) => (
        <Typography variant="body2" dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {voucher.phone}
        </Typography>
      ),
    },
    {
      id: 'workshop',
      alignment: 'center',
      label: t('orders.workshop'),
      render: (voucher: VoucherOut) => (
        <WorkshopCircle account={account} orderPageId={voucher.order_page_id} language={language} withTooltip />
      ),
    },
    {
      id: TABLE_PRICE_ID,
      alignment: 'center',
      label: t('vouchers.price'),
    },
    {
      id: 'balance',
      alignment: 'center',
      label: t('vouchers.balance'),
      render: (voucher: VoucherOut) =>
        voucher.type === VoucherType.MONETARY && !readOnly ? (
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {t('vouchers.balanceOutOf', {
              balance: customerFacingPriceFormatter(voucher.balance ?? 0, account),
              amount: customerFacingPriceFormatter(voucher.price, account),
            })}
          </Typography>
        ) : (
          ''
        ),
    },
    {
      id: 'duration',
      alignment: 'center',
      label: t('orders.duration'),
      render: (voucher: VoucherOut) => TimeUtils.formatDuration(voucher.duration, t),
    },
    {
      id: 'order_number',
      alignment: 'center',
      label: t('order_number'),
      render: (voucher: VoucherOut) => `#${voucher.order_number}`,
    },
    {
      id: 'paymentStatus',
      alignment: 'center',
      label: t('vouchers.origin'),
      render: (voucher: VoucherOut) => getVoucherPaymentStatusTextOrIcon(voucher.payment_status, t),
    },
    {
      id: 'status',
      alignment: 'center',
      label: t('vouchers.status'),
      render: (voucher: VoucherOut) => getVoucherStatus(voucher, account, t),
    },
    {
      id: 'orderlink',
      alignment: 'center',
      label: t('vouchers.orderlink'),
      render: (voucher: VoucherOut) =>
        voucher.visits_used?.[0] ? (
          <Tooltip title={t('vouchers.openRedeemedOrder')} arrow>
            <IconButton onClick={() => openRedeemedOrderDrawer(voucher.visits_used?.[0] as string)}>
              <LinkedOrderIcon width={24} height={24} />
            </IconButton>
          </Tooltip>
        ) : (
          ''
        ),
    },
    {
      id: 'comment',
      alignment: 'center',
      label: t('comment'),
      render: (voucher: VoucherOut) => <Typography>{voucher.comment?.slice(0, 30)}</Typography>,
    },
    {
      id: 'actions',
      alignment: 'center',
      width: 168,
      label: t('vouchers.details'),
      render: (voucher: VoucherOut) => {
        const shouldDisableActions = voucher.status !== VoucherStatus.ACTIVE || !isAtLeastSupervisor;
        const voucherLink = voucher.redemption_link || '';

        return (
          <Grid container direction="row" justifyContent="flex-end" padding="2px">
            <Tooltip title={voucher.status !== VoucherStatus.ACTIVE ? t('vouchers.redeemdisabled') : t('redeem')} arrow>
              <span>
                <IconButton
                  onClick={() => openEditOrderDrawer({ orderPageId: voucher.order_page_id, voucherToRedeem: voucher })}
                  disabled={shouldDisableActions}>
                  <Redeem />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={voucher.status !== VoucherStatus.ACTIVE ? t('vouchers.redeemdisabled') : t('redeemptionLink')}
              arrow>
              <Grid container width="27px" alignItems="center" mx={1}>
                <CopyToClipboardButton
                  disabled={shouldDisableActions}
                  alwaysShorten
                  orderPageId={voucher.order_page_id}
                  text={voucherLink}
                  IconComponent={MuiLinkIcon}
                  color={voucher.status !== VoucherStatus.ACTIVE ? theme.palette.text.secondary : undefined}
                />
              </Grid>
            </Tooltip>
            <Tooltip title={shouldDisableActions ? t('noPermissions') : t('sendMessage')} arrow>
              <span>
                <IconButton
                  disabled={shouldDisableActions}
                  onClick={() => {
                    setSelectedVoucher(voucher);
                    setSendToEmail(voucher.email);
                    setSendVoucherDialogIsOpen(true);
                  }}>
                  <Email />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={voucher.status !== VoucherStatus.ACTIVE ? t('vouchers.deleteDisabled') : t('vouchers.archive')}
              arrow>
              <span>
                <IconButton
                  onClick={() => {
                    setSelectedVoucher(voucher);
                    setDeleteVoucherIsOpen(true);
                  }}
                  disabled={shouldDisableActions}>
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        );
      },
    },
  ];
  const { state: filterState } = useVouchersFilter();
  const [searchParams] = useSearchParams();
  const initialSearchValue = searchParams.get('search') ?? undefined;
  const voucherIdToOpen = searchParams.get('entityid');
  useEffect(() => {
    if (!voucherIdToOpen) return;
    VoucherService.getVoucherById(voucherIdToOpen)
      .then((voucher) =>
        openEditOrderDrawer({ orderPageId: voucher.order_page_id, createVoucher: false, voucherToEdit: voucher }),
      )
      .catch((error) => console.error('Error loading voucher from url param:', error));
  }, []);
  const {
    mutate: mutateVouchers,
    data,
    TableComponent,
  } = useTable<VoucherOut>({
    actionsComponent: <VouchersActions />,
    opId: 'getVouchers',
    fetcher: voucherFetcher,
    tableDetails,
    filterState,
    fillHeight: true,
    initialSearchValue,
  });
  const vouchersList = data || [];

  const archiveVoucher = async () => {
    if (selectedVoucher) {
      await VoucherService.archiveVoucher(selectedVoucher.id);
      const otherVouchers = vouchersList.filter((x) => x.id !== selectedVoucher.id);
      await mutateVouchers([...otherVouchers, { ...selectedVoucher, status: VoucherStatus.ARCHIVED }]);
      setRefundOffer({
        ownerType: RefundOwnerType.VOUCHER,
        ownerId: selectedVoucher.id,
        title: t('refunds.offer.voucherTitle'),
        customerName: `${selectedVoucher.firstname} ${selectedVoucher.lastname}`,
        ownerLabel: t('refunds.dialog.voucherTitle', { number: selectedVoucher.order_number }),
        voucherBalance: selectedVoucher.balance,
      });
    }
  };
  const redeemVoucherCallback = (voucher: VoucherOut) => {
    const otherVouchers = vouchersList.filter((x) => x.id !== voucher.id);
    void mutateVouchers([...otherVouchers, { ...voucher, status: VoucherStatus.USED }]);
  };
  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
      {TableComponent}
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={() => {
          void mutateVouchers();
          void closeEditOrderDrawer();
        }}
        redeemVoucherCallback={redeemVoucherCallback}
      />
      <ConfirmationDialog
        title={t('vouchers.archiveVoucher.title')}
        content={<Typography>{t('vouchers.archiveVoucher.description')}</Typography>}
        isOpen={deleteVoucherIsOpen}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed && selectedVoucher) {
            await archiveVoucher();
          }
          setDeleteVoucherIsOpen(false);
        }}
      />
      {refundOffer && <RefundOfferDialog isOpen onClose={() => setRefundOffer(undefined)} {...refundOffer} />}
      <ConfirmationDialog
        loadingButton
        ctaText={t('vouchers.sendVoucher.sendEmail')}
        confirmButtonColor="primary"
        title={t('vouchers.sendVoucher.title')}
        content={
          <Grid container flexDirection="column">
            <Typography>{t('vouchers.sendVoucher.chooseEmail')}</Typography>
            <Grid maxWidth="200px" mt={2}>
              <TextField size="small" value={sendToEmail} onChange={(e) => setSendToEmail(e.target.value)} />
            </Grid>
          </Grid>
        }
        isOpen={sendVoucherDialogIsOpen}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed && selectedVoucher) {
            await VoucherService.sendVoucher({ voucher_id: selectedVoucher.id, email: sendToEmail });
            setEmailSentIsOpen(true);
          }
          setSendVoucherDialogIsOpen(false);
        }}
      />
      <Snackbar
        open={emailSentIsOpen}
        onClose={() => setEmailSentIsOpen(false)}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setEmailSentIsOpen(false)} severity="success">
          {t('vouchers.sendVoucher.emailSentSuccess')}
        </Alert>
      </Snackbar>
    </Grid>
  );
});

export default VouchersTable;
